import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import API from "../lib/api";
import { isTimeoutError, withTimeout } from "../lib/api";
import {
  Users, Shield, AlertTriangle, CheckCircle, ArrowRight,
  Loader2, Search, Activity, FileText, DollarSign, Settings,
  UserCheck, FileSearch, Server, Gavel, Lightbulb, Flag,
  ChevronRight, AlertCircle, Scale, Layers, BarChart3, Brain
} from "lucide-react";

const AGENT_MAP = {
  lead_auditor: {
    icon: UserCheck,
    color: "emerald",
    gradient: "from-emerald-500/10 via-emerald-500/5 to-transparent",
    border: "border-emerald-500/30",
    bg: "bg-emerald-50 dark:bg-emerald-950/20",
    dot: "bg-emerald-500",
  },
  skeptical_watchdog: {
    icon: AlertTriangle,
    color: "amber",
    gradient: "from-amber-500/10 via-amber-500/5 to-transparent",
    border: "border-amber-500/30",
    bg: "bg-amber-50 dark:bg-amber-950/20",
    dot: "bg-amber-500",
  },
  detail_perfectionist: {
    icon: FileSearch,
    color: "blue",
    gradient: "from-blue-500/10 via-blue-500/5 to-transparent",
    border: "border-blue-500/30",
    bg: "bg-blue-50 dark:bg-blue-950/20",
    dot: "bg-blue-500",
  },
  compliance_guardian: {
    icon: Shield,
    color: "purple",
    gradient: "from-purple-500/10 via-purple-500/5 to-transparent",
    border: "border-purple-500/30",
    bg: "bg-purple-50 dark:bg-purple-950/20",
    dot: "bg-purple-500",
  },
  it_specialist: {
    icon: Server,
    color: "cyan",
    gradient: "from-cyan-500/10 via-cyan-500/5 to-transparent",
    border: "border-cyan-500/30",
    bg: "bg-cyan-50 dark:bg-cyan-950/20",
    dot: "bg-cyan-500",
  },
};

const STATUS_COLORS = {
  idle: "bg-slate-400",
  analyzing: "bg-amber-500 animate-pulse",
  has_findings: "bg-emerald-500",
  clear: "bg-cyan-500",
  needs_evidence: "bg-orange-500",
  muted: "bg-slate-400",
  challenging: "bg-red-500 animate-pulse",
  rebutting: "bg-blue-500 animate-pulse",
  consolidating: "bg-emerald-500 animate-pulse",
  escalated: "bg-red-600",
};

const STATUS_LABELS = {
  idle: "Idle",
  analyzing: "Analyzing Evidence",
  has_findings: "Findings Raised",
  clear: "No Issues",
  needs_evidence: "Needs Evidence",
  muted: "Muted",
  challenging: "Challenging",
  rebutting: "Rebuttal",
  consolidating: "Consolidating",
  escalated: "Escalated",
};

const SEVERITY_STYLES = {
  Critical: "bg-red-600 text-white border-red-700",
  High: "bg-red-500 text-white border-red-400",
  Medium: "bg-amber-500 text-white border-amber-400",
  Low: "bg-blue-500 text-white border-blue-400",
  Observation: "bg-slate-500 text-white border-slate-400",
};

const CATEGORY_CONFIG = {
  operational: { icon: Settings, label: "Operational", color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950/30" },
  financial: { icon: DollarSign, label: "Financial", color: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-950/30" },
  compliance: { icon: Shield, label: "Compliance", color: "text-purple-600", bg: "bg-purple-50 dark:bg-purple-950/30" },
  it: { icon: Server, label: "IT", color: "text-cyan-600", bg: "bg-cyan-50 dark:bg-cyan-950/30" },
};

function AgentCard({ agent, observations }) {
  const aid = agent.agent_id || "";
  const agentStyle = AGENT_MAP[aid] || AGENT_MAP.lead_auditor;
  const IconComponent = agentStyle.icon;
  const agentObs = observations.filter((o) => o.agent_id === aid);
  const statusColor = STATUS_COLORS[agent.status] || STATUS_COLORS.idle;
  const statusLabel = STATUS_LABELS[agent.status] || "Unknown";

  return (
    <Card className={`border-l-4 ${agentStyle.border} ${agentStyle.bg} overflow-hidden`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${agentStyle.bg} border ${agentStyle.border}`}>
              <IconComponent className={`w-5 h-5 text-${agentStyle.color}-600`} />
            </div>
            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${statusColor} ring-2 ring-white dark:ring-slate-900`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{agent.name}</span>
              <Badge variant="outline" className="text-[10px] h-4 px-1.5 uppercase tracking-wider">{agent.role?.replace(/_/g, ' ') || 'Analyst'}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{statusLabel}</p>
            {agent.persona_summary && (
              <p className="text-[11px] text-muted-foreground/70 mt-1.5 line-clamp-2 leading-relaxed">
                {agent.persona_summary}
              </p>
            )}
            {agentObs.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="font-medium text-slate-700 dark:text-slate-300">{agentObs.length} observation{agentObs.length !== 1 ? 's' : ''}</span>
                {agentObs.some(o => o.risk_rating === "High" || o.risk_rating === "Critical") && (
                  <Badge variant="outline" className="text-[10px] text-red-600 border-red-300 bg-red-50 dark:bg-red-950/30">
                    <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> High Risk
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, accent }) {
  return (
    <Card className={`border-l-4 ${accent || "border-l-slate-400"} bg-white dark:bg-slate-900 shadow-sm`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
          {Icon && <Icon className={`w-4 h-4 ${color || "text-muted-foreground"}`} />}
        </div>
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function FindingCard({ finding }) {
  const sev = finding.final_severity || finding.suggested_severity || finding.severity || "Medium";
  const category = (finding.category || "operational").toLowerCase();
  const catConfig = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.operational;
  const CatIcon = catConfig.icon;

  return (
    <Card className="border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${catConfig.bg} shrink-0`}>
            <CatIcon className={`w-4 h-4 ${catConfig.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{finding.finding_title}</span>
              <Badge className={`text-[10px] px-1.5 ${SEVERITY_STYLES[sev] || SEVERITY_STYLES.Medium}`}>{sev}</Badge>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">{finding.finding_description}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {finding.control_id && (
                <Badge variant="secondary" className="text-[10px] font-mono">{finding.control_id}</Badge>
              )}
              {finding.disputed && (
                <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                  <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Disputed
                </Badge>
              )}
              {finding.status === "needs_more_evidence" && (
                <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-400 bg-orange-50 dark:bg-orange-950/30">
                  <AlertCircle className="w-2.5 h-2.5 mr-0.5" /> Needs Evidence
                </Badge>
              )}
              {finding.contributing_agents?.length > 0 && (
                <Badge variant="outline" className="text-[10px]">
                  <Users className="w-2.5 h-2.5 mr-0.5" /> {finding.contributing_agents.length} agents
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Phase7TeamMode() {
  const navigate = useNavigate();
  const { auditRun, advancePhase } = useAudit();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");

  const runId = auditRun?.id;
  const isTeamMode = auditRun?.audit_mode === "team";

  const fetchDashboard = useCallback(async () => {
    if (!runId) return;
    try {
      const { data } = await API.get(`/audit-runs/${runId}/team/phase7-dashboard`);
      setDashboardData(data);
    } catch (error) {
      console.error("Failed to fetch phase 7 dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleRunEvaluation = async () => {
    if (!runId) return;
    try {
      setEvaluating(true);
      setProgress(10);
      setProgressLabel("Starting Phase 7 team evaluation...");

      const { data } = await API.post(
        `/audit-runs/${runId}/team/phase7-evaluation`,
        {},
        withTimeout()
      );

      if (!data?.job_id) {
        setEvaluating(false);
        setProgress(0);
        toast.error(data?.message || "Failed to start evaluation");
        return;
      }

      setJobId(data.job_id);
      setProgress(15);
      setProgressLabel("Queued — agents preparing analysis...");

      const pollStartedAt = Date.now();
      const POLL_INTERVAL = 2000;
      const MAX_WAIT = 45 * 60 * 1000;

      const pollJob = async () => {
        try {
          const { data: status } = await API.get(`/job-status/${data.job_id}`);

          if (status.state === "PENDING") {
            setProgress((p) => Math.max(p, 15));
          } else if (status.state === "PROGRESS") {
            const step = status.meta?.step;
            if (step === "agent_analysis") {
              setProgress((p) => Math.max(p, 35));
              setProgressLabel(status.meta?.status || "Agents analyzing evidence...");
            } else if (step === "consolidating") {
              setProgress((p) => Math.max(p, 70));
              setProgressLabel("Consolidating findings across agents...");
            }
          } else if (status.state === "SUCCESS") {
            setProgress(100);
            setProgressLabel("Evaluation complete");
            setEvaluating(false);
            setJobId(null);
            await fetchDashboard();
            toast.success("Phase 7 team evaluation completed");
            return;
          } else if (status.state === "FAILURE" || status.state === "REVOKED") {
            setEvaluating(false);
            setJobId(null);
            setProgress(0);
            toast.error(status.error || "Evaluation failed");
            return;
          }

          if (Date.now() - pollStartedAt >= MAX_WAIT) {
            setEvaluating(false);
            setJobId(null);
            setProgress(0);
            toast.error("Evaluation timed out");
            return;
          }

          setTimeout(pollJob, POLL_INTERVAL);
        } catch (error) {
          console.error("Poll error:", error);
          setTimeout(pollJob, POLL_INTERVAL * 2);
        }
      };

      setTimeout(pollJob, POLL_INTERVAL);
    } catch (error) {
      setEvaluating(false);
      setProgress(0);
      toast.error(isTimeoutError(error) ? "Request timed out" : "Failed to start evaluation");
    }
  };

  const handleContinue = async () => {
    await advancePhase(8);
    navigate("/phase/8");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const agents = dashboardData?.agent_contributions || [];
  const observations = dashboardData?.agent_observations || [];
  const findings = dashboardData?.consolidated_findings || [];
  const remediationActions = stats.remediation_actions || [];
  const executiveSummary = dashboardData?.team_executive_summary || "";
  const evaluationStatus = dashboardData?.phase7_evaluation?.status;

  const operationalFindings = findings.filter(
    (f) => (f.category || "operational").toLowerCase() === "operational"
  );
  const financialFindings = findings.filter(
    (f) => (f.category || "").toLowerCase() === "financial"
  );
  const otherFindings = findings.filter(
    (f) => {
      const cat = (f.category || "operational").toLowerCase();
      return cat !== "operational" && cat !== "financial";
    }
  );

  return (
    <div className="space-y-6" data-testid="phase-7-team-mode">
      {/* Header */}
      <div className="overflow-hidden rounded-2xl border p-5 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 hover:bg-indigo-100">
                <Brain className="mr-1 h-3 w-3" /> Multi-Agent Analysis
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-100">
                <Users className="mr-1 h-3 w-3" /> Team Mode Active
              </Badge>
              {evaluationStatus === "running" && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
                  <Activity className="mr-1 h-3 w-3 animate-pulse" /> Evaluation Running
                </Badge>
              )}
            </div>
            <h2 className="text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">
              Evidence Evaluation — Team Dashboard
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Multi-agent team analyzing evidence with cross-validation, skeptic review, and consolidated findings.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleRunEvaluation}
              disabled={evaluating}
              className="bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-900/20"
            >
              {evaluating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Evaluating...</>
              ) : (
                <><Search className="mr-2 h-4 w-4" /> Run Team Evaluation</>
              )}
            </Button>
            <Button
              onClick={handleContinue}
              disabled={findings.length === 0}
              variant="outline"
              className="border-slate-200"
            >
              <ArrowRight className="mr-2 h-4 w-4" /> Continue to Findings
            </Button>
          </div>
        </div>

        {/* Progress bar during evaluation */}
        {evaluating && (
          <div className="mt-4 space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{progressLabel}</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
        <StatCard
          icon={Lightbulb}
          label="High-Confidence Insights"
          value={stats.high_confidence_insights || 0}
          sub="Confidence >= 70%"
          color="text-amber-500"
          accent="border-l-amber-500"
        />
        <StatCard
          icon={Flag}
          label="Total Findings"
          value={stats.consolidated_findings_count || 0}
          sub="Team-consolidated"
          color="text-red-500"
          accent="border-l-red-500"
        />
        <StatCard
          icon={AlertCircle}
          label="Disputed"
          value={stats.disputed_count || 0}
          sub={stats.disputed_count > 0 ? "Awaiting resolution" : "No disputes"}
          color="text-amber-500"
          accent="border-l-amber-500"
        />
        <StatCard
          icon={BarChart3}
          label="Operational"
          value={stats.operational_count || 0}
          sub={stats.operational_count > 0 ? `${stats.operational_count} finding(s)` : "No operational findings"}
          color="text-blue-500"
          accent="border-l-blue-500"
        />
        <StatCard
          icon={DollarSign}
          label="Financial"
          value={stats.financial_count || 0}
          sub={stats.financial_count > 0 ? `${stats.financial_count} finding(s)` : "No financial findings"}
          color="text-emerald-500"
          accent="border-l-emerald-500"
        />
      </div>

      {/* Main Content: 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Panel — Team Composition */}
        <div className="lg:col-span-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-500" />
              Audit Team ({agents.length})
            </h3>
            <Badge variant="outline" className="text-[10px]">
              {observations.length} total observations
            </Badge>
          </div>
          <div className="space-y-3">
            {agents.map((agent) => (
              <AgentCard
                key={agent.agent_id}
                agent={agent}
                observations={observations}
              />
            ))}
          </div>

          {/* Evidence summary card */}
          {executiveSummary && (
            <Card className="bg-gradient-to-br from-indigo-500/5 to-purple-500/5 border-indigo-500/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-indigo-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-1">Team Executive Summary</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{executiveSummary}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel — Consolidated Results */}
        <div className="lg:col-span-8 space-y-4">
          {/* Findings by severity distribution */}
          {Object.keys(stats.findings_by_severity || {}).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-muted-foreground">Severity Distribution:</span>
              {Object.entries(stats.findings_by_severity || {}).map(([sev, count]) => (
                <Badge key={sev} className={`text-xs ${SEVERITY_STYLES[sev] || SEVERITY_STYLES.Medium}`}>
                  {count} {sev}
                </Badge>
              ))}
            </div>
          )}

          {/* Operational Findings */}
          {operationalFindings.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-4 h-4 text-blue-500" />
                <h3 className="text-sm font-semibold">Operational Findings ({operationalFindings.length})</h3>
              </div>
              <div className="space-y-2">
                {operationalFindings.map((f) => (
                  <FindingCard key={f.id} finding={f} />
                ))}
              </div>
            </div>
          )}

          {/* Financial Findings */}
          {financialFindings.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                <h3 className="text-sm font-semibold">Financial Findings ({financialFindings.length})</h3>
              </div>
              <div className="space-y-2">
                {financialFindings.map((f) => (
                  <FindingCard key={f.id} finding={f} />
                ))}
              </div>
            </div>
          )}

          {/* Other Findings */}
          {otherFindings.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-4 h-4 text-slate-500" />
                <h3 className="text-sm font-semibold">Other Findings ({otherFindings.length})</h3>
              </div>
              <div className="space-y-2">
                {otherFindings.map((f) => (
                  <FindingCard key={f.id} finding={f} />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {findings.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Brain className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="text-sm font-semibold text-muted-foreground mb-1">No Findings Yet</h3>
                <p className="text-xs text-muted-foreground/70 max-w-md mx-auto">
                  Run the team evaluation to have agents analyze evidence, cross-validate findings,
                  and generate consolidated results with severity ratings and remediation suggestions.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Remediation Actions */}
          {remediationActions.length > 0 && (
            <Card className="bg-gradient-to-br from-blue-500/5 to-indigo-500/5 border-blue-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-blue-500" />
                  Suggested Remediation Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-48">
                  <div className="space-y-2">
                    {remediationActions.map((action, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <ChevronRight className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                        <span className="text-xs text-muted-foreground">{action}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Severity legend */}
          {findings.length > 0 && (
            <div className="flex items-center gap-4 text-[10px] text-muted-foreground pt-2">
              <span className="font-medium">Severity Legend:</span>
              {Object.entries(SEVERITY_STYLES).map(([sev, cls]) => (
                <Badge key={sev} className={`text-[9px] px-1.5 ${cls}`}>{sev}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <p className="text-xs text-muted-foreground">
          {findings.length > 0
            ? `${findings.length} consolidated finding(s) — review and continue to Phase 8`
            : 'Run team evaluation to generate consolidated findings'}
        </p>
        <Button onClick={handleContinue} disabled={findings.length === 0}>
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Findings
        </Button>
      </div>
    </div>
  );
}
